import { Badge, Button, Popover } from 'antd';
import React from 'react';
import {
    ArenaLinkState,
    connectivityMonitor,
    linkLabel,
    pendingLabel,
} from '../helpers/connectivityMonitor';
import { eventSubmissionQueue } from '../helpers/eventSubmissionQueue';
import { OutboxRecord } from '../helpers/offlineEventStore';

interface Props {
    matchId: number;
    /** Dark mobile report vs desktop. */
    variant?: 'mobile' | 'desktop';
}

interface State {
    link: ArenaLinkState;
    pending: OutboxRecord[];
    open: boolean;
}

/**
 * Arena reachability, hub state, and the pending outbox count for this match.
 */
class OfflineStatusControl extends React.Component<Props, State> {
    private unsubConnectivity: (() => void) | null = null;

    private releaseMatchLog: (() => void) | null = null;

    private unsubOutbox: (() => void) | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            link: connectivityMonitor.getLinkState(),
            pending: [],
            open: false,
        };
    }

    public componentDidMount = (): void => {
        this.releaseMatchLog = connectivityMonitor.retainMatchLog();
        eventSubmissionQueue.start();
        this.unsubConnectivity = connectivityMonitor.subscribeLink((link: ArenaLinkState) => {
            this.setState({ link });
        });
        this.unsubOutbox = eventSubmissionQueue.subscribe((e): void => {
            if (e.type === 'changed' || e.type === 'published' || e.type === 'cancelled') {
                void this.refreshPending();
            }
        });
        void this.refreshPending();
    };

    public componentDidUpdate = (prev: Props): void => {
        if (prev.matchId !== this.props.matchId) {
            void this.refreshPending();
        }
    };

    public componentWillUnmount = (): void => {
        this.unsubConnectivity?.();
        this.unsubOutbox?.();
        this.releaseMatchLog?.();
    };

    private refreshPending = async (): Promise<void> => {
        const pending = await eventSubmissionQueue.listByMatch(this.props.matchId);
        this.setState({ pending });
    };

    private ageLabel = (createdAt: number): string => {
        const sec = Math.max(0, Math.floor((Date.now() - createdAt) / 1000));
        if (sec < 60) return `${sec}s ago`;
        const min = Math.floor(sec / 60);
        if (min < 60) return `${min}m ago`;
        return `${Math.floor(min / 60)}h ago`;
    };

    private content = (): React.ReactElement => {
        const { pending, link } = this.state;
        const canSync = connectivityMonitor.getCanFlush();
        return (
            <div className="offline-status-popover">
                <div className="offline-status-popover__head">
                    {linkLabel(link)} · {pendingLabel(pending.length)}
                </div>
                {pending.length === 0 ? (
                    <div className="offline-status-popover__empty">No queued events.</div>
                ) : (
                    <ul className="offline-status-popover__list">
                        {pending.map((row) => (
                            <li key={row.id}>
                                <span className="offline-status-popover__op">{row.op}</span>
                                <span className="offline-status-popover__label">{row.label}</span>
                                <span className="offline-status-popover__age">
                                    {this.ageLabel(row.createdAt)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                <Button
                    size="small"
                    block
                    type="primary"
                    disabled={!canSync}
                    onClick={(e): void => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Shared by desktop + mobile: clears holds, resets attempt
                        // budget, probes Arena, drains outbox, then screens refetch.
                        eventSubmissionQueue.forceFlush();
                        this.setState({ open: false });
                    }}
                >
                    Sync now
                </Button>
            </div>
        );
    };

    public render = (): React.ReactElement => {
        const { variant = 'desktop' } = this.props;
        const { link, pending, open } = this.state;
        const count = pending.length;
        const label = linkLabel(link);
        const tone =
            link === 'online' ? 'online' : link === 'reconnecting' ? 'reconnecting' : 'unreachable';
        const icon = (
            <button
                type="button"
                className={`offline-status-btn offline-status-btn--${variant} offline-status-btn--${tone}`}
                aria-label={`${label}, ${pendingLabel(count)}`}
                title={`${label}, ${pendingLabel(count)}`}
            >
                <span className="offline-status-btn__dot" />
                <span className="offline-status-btn__text">{label}</span>
                <span className="offline-status-btn__pending">{pendingLabel(count)}</span>
            </button>
        );

        return (
            <Popover
                content={this.content()}
                trigger="click"
                visible={open}
                onVisibleChange={(v): void => this.setState({ open: v })}
                placement="bottomRight"
            >
                <Badge count={count} size="small" offset={[-2, 2]}>
                    {icon}
                </Badge>
            </Popover>
        );
    };
}

export default OfflineStatusControl;
